import { Icon } from '@/components/ui/Icon';
import { mdiArrowDown, mdiArrowUp } from '@mdi/js';
import * as React from 'react';

interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  className?: string;
  width?: string;
}

export interface AdvancedTableProps<T = Record<string, unknown>> {
  columns: TableColumn[];
  data: T[];
  loading?: boolean;
  onSort?: (key: string) => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  renderCell?: (key: string, value: T[keyof T], item: T) => React.ReactNode;
  renderActions?: (item: T) => React.ReactNode;
  onRowClick?: (item: T) => void;
  selectable?: boolean;
  selectedItems?: T[];
  onSelectionChange?: (items: T[]) => void;
  emptyMessage?: string;
  className?: string;
}

export const AdvancedTable = <T = Record<string, unknown>,>({
  columns,
  data,
  loading = false,
  onSort,
  sortKey,
  sortDirection = 'asc',
  renderCell,
  renderActions,
  onRowClick,
  selectable = false,
  selectedItems = [],
  onSelectionChange,
  emptyMessage = 'No data available',
  className,
}: AdvancedTableProps<T>) => {
  const handleSort = (key: string) => {
    if (onSort) {
      onSort(key);
    }
  };

  const handleRowClick = (item: T, event: React.MouseEvent) => {
    // Don't trigger row click if clicking on action buttons or checkboxes
    if (
      (event.target as HTMLElement).closest('button') ||
      (event.target as HTMLElement).closest('input[type="checkbox"]')
    ) {
      return;
    }

    if (onRowClick) {
      onRowClick(item);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (onSelectionChange) {
      onSelectionChange(checked ? data : []);
    }
  };

  const handleSelectItem = (item: T, checked: boolean) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange([...selectedItems, item]);
      } else {
        onSelectionChange(selectedItems.filter(selected => selected !== item));
      }
    }
  };

  const isSelected = (item: T) => {
    return selectedItems.some(selected => selected === item);
  };

  const allSelected = data.length > 0 && selectedItems.length === data.length;
  const someSelected =
    selectedItems.length > 0 && selectedItems.length < data.length;

  if (loading) {
    const skeletonRows = 5;
    return (
      <div className={`overflow-x-auto ${className}`}>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-grey-200 bg-grey-50'>
              {selectable && <th className='px-6 py-4 text-left w-12'></th>}
              {columns.map(column => (
                <th
                  key={column.key}
                  className={`px-6 py-4 text-left ${column.className || ''}`}
                  style={column.width ? { width: column.width } : undefined}
                >
                  <div className='h-4 bg-grey-200 rounded animate-pulse w-16'></div>
                </th>
              ))}
              {renderActions && (
                <th className='px-6 py-4 text-left'>
                  <div className='h-4 bg-grey-200 rounded animate-pulse w-16'></div>
                </th>
              )}
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-grey-200'>
            {Array.from({ length: skeletonRows }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {selectable && (
                  <td className='px-6 py-4'>
                    <div className='h-4 w-4 bg-grey-200 rounded animate-pulse'></div>
                  </td>
                )}
                {columns.map(column => (
                  <td
                    key={column.key}
                    className={`px-6 py-4 ${column.className || ''}`}
                  >
                    {column.className?.includes('w-14') ? (
                      <div className='h-10 w-10 bg-grey-200 rounded-full animate-pulse'></div>
                    ) : (
                      <div className='h-4 bg-grey-200 rounded animate-pulse'></div>
                    )}
                  </td>
                ))}
                {renderActions && (
                  <td className='px-6 py-4'>
                    <div className='flex items-center space-x-2'>
                      <div className='h-8 w-8 bg-grey-200 rounded animate-pulse'></div>
                      <div className='h-8 w-8 bg-grey-200 rounded animate-pulse'></div>
                      <div className='h-8 w-8 bg-grey-200 rounded animate-pulse'></div>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <p className='text-grey-500 text-lg mb-2'>📭</p>
          <p className='text-grey-500'>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className='w-full'>
        <thead>
          <tr className='border-b border-grey-200 bg-grey-50'>
            {selectable && (
              <th className='px-6 py-4 text-left w-12'>
                <input
                  type='checkbox'
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={e => handleSelectAll(e.target.checked)}
                  className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-grey-300 rounded'
                />
              </th>
            )}
            {columns.map(column => (
              <th
                key={column.key}
                className={`px-6 py-4 text-left text-xs font-medium text-grey-500 uppercase tracking-wider ${
                  column.className || ''
                } ${column.sortable ? 'cursor-pointer hover:bg-grey-100' : ''}`}
                onClick={() => column.sortable && handleSort(column.key)}
                style={column.width ? { width: column.width } : undefined}
              >
                <div className='flex items-center space-x-1'>
                  <span>{column.label}</span>
                  {column.sortable && sortKey === column.key && (
                    <Icon
                      path={sortDirection === 'asc' ? mdiArrowUp : mdiArrowDown}
                      size={16}
                      className='text-blue-500'
                    />
                  )}
                </div>
              </th>
            ))}
            {renderActions && (
              <th className='px-6 py-4 text-left text-xs font-medium text-grey-500 uppercase tracking-wider'>
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className='bg-white divide-y divide-grey-200'>
          {data.map((item, index) => (
            <tr
              key={index}
              className={`
                transition-colors duration-200
                ${onRowClick ? 'cursor-pointer hover:bg-grey-50' : ''}
                ${isSelected(item) ? 'bg-blue-50' : ''}
              `}
              onClick={e => handleRowClick(item, e)}
            >
              {selectable && (
                <td className='px-6 py-4 whitespace-nowrap'>
                  <input
                    type='checkbox'
                    checked={isSelected(item)}
                    onChange={e => handleSelectItem(item, e.target.checked)}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-grey-300 rounded'
                  />
                </td>
              )}
              {columns.map(column => (
                <td
                  key={column.key}
                  className={`px-6 py-4 whitespace-nowrap text-sm text-grey-900 ${
                    column.className || ''
                  }`}
                >
                  {renderCell
                    ? renderCell(column.key, item[column.key as keyof T], item)
                    : (item[column.key as keyof T] as React.ReactNode)}
                </td>
              ))}
              {renderActions && (
                <td className='px-6 py-4 whitespace-nowrap text-left text-sm font-medium'>
                  {renderActions(item)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
